import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { deleteFile, deleteFolder, uploadFile, createFolder, uploadFileToUrl, deleteFileFromUrl, uploadFileToGeneralFiles } from '../api/fileApi'
import LogTool from '../../../logger/logTools'
import { type FileInfo } from '../../../utils/types'

const log = new LogTool({ context: 'FilesystemMutations', enable: true, logLevel: 'warn' })

/**
 * Hook to delete a file
 */
export function useDeleteFile(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteFile,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['filesystem'] })
      log.info('File deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting file:', error)
    },
  })
}

/**
 * Hook to delete a folder
 */
export function useDeleteFolder(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteFolder,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['filesystem'] })
      log.info('Folder deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting folder:', error)
    },
  })
}

/**
 * Hook to upload a file
 */
export function useUploadFile(): UseMutationResult<
  any,
  Error,
  { path: string; file: File }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ path, file }) => uploadFile(path, file),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['filesystem'] })
      log.info('File uploaded successfully')
    },
    onError: (error) => {
      log.error('Error uploading file:', error)
    },
  })
}

/**
 * Hook to create a folder
 */
export function useCreateFolder(): UseMutationResult<
  any,
  Error,
  { path: string; folderName: string }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ path, folderName }) => createFolder(path, folderName),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['filesystem'] })
      log.info('Folder created successfully')
    },
    onError: (error) => {
      log.error('Error creating folder:', error)
    },
  })
}

/**
 * Hook to upload a file to a specific URL endpoint
 */
export function useUploadFileToUrl(): UseMutationResult<
  FileInfo,
  Error,
  { url: string; file: File }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ url, file }) => uploadFileToUrl(url, file),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['files'] })
      await queryClient.invalidateQueries({ queryKey: ['uploadedFiles', variables.url] })
      log.info('File uploaded successfully')
    },
    onError: (error) => {
      log.error('Error uploading file:', error)
    },
  })
}

/**
 * Hook to delete a file from a specific URL
 */
export function useDeleteFileFromUrl(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteFileFromUrl,
    onSuccess: async (_, url) => {
      await queryClient.invalidateQueries({ queryKey: ['files'] })
      await queryClient.invalidateQueries({ queryKey: ['uploadedFiles'] })
      log.info('File deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting file:', error)
    },
  })
}

/**
 * Hook to upload a file to general filesystem (used by S3FileUpload)
 */
export function useUploadFileToGeneralFiles(): UseMutationResult<
  any,
  Error,
  { path: string; file: File }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ path, file }) => uploadFileToGeneralFiles(path, file),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['filesystem'] })
      log.info('File uploaded successfully')
    },
    onError: (error) => {
      log.error('Error uploading file:', error)
    },
  })
}

